// Thank you Mr. Claude for the code snippet
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

const calculateSlice = (
  center: number,
  percentage: number,
  startAngle: number,
  isHovered = false,
) => {
  const radius = 40;
  const innerRadius = 25;

  const currentRadius = isHovered ? radius * 1.1 : radius;

  const angle = percentage === 100 ? 359.99 : (percentage / 100) * 360;
  const endAngle = startAngle + angle;

  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  const x1 = center + currentRadius * Math.cos(startRad);
  const y1 = center + currentRadius * Math.sin(startRad);
  const x2 = center + currentRadius * Math.cos(endRad);
  const y2 = center + currentRadius * Math.sin(endRad);

  const x3 = center + innerRadius * Math.cos(endRad);
  const y3 = center + innerRadius * Math.sin(endRad);
  const x4 = center + innerRadius * Math.cos(startRad);
  const y4 = center + innerRadius * Math.sin(startRad);

  const largeArcFlag = angle > 180 ? 1 : 0;

  return `M ${x1},${y1}
          A ${currentRadius},${currentRadius} 0 ${largeArcFlag} 1 ${x2},${y2}
          L ${x3},${y3}
          A ${innerRadius},${innerRadius} 0 ${largeArcFlag} 0 ${x4},${y4}
          Z`;
};

interface Props {
  data: [number, number];
}

// TODO - Use Shadcn-UI charts once migrated away from Next.js
export const PieChart = ({ data }: Props): JSX.Element => {
  const [managed, unmanaged] = data;
  const total = managed + unmanaged;

  const { managedPercentage, unmanagedPercentage } =
    total === 0
      ? { managedPercentage: 0, unmanagedPercentage: 100 }
      : {
          managedPercentage: (managed / total) * 100,
          unmanagedPercentage: (unmanaged / total) * 100,
        };

  const viewBoxSize = 100;
  const center = viewBoxSize / 2;

  const [cursor, setCursor] = useState<{
    x: number;
    y: number;
    managed: boolean;
  }>();

  return (
    <>
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="w-full h-full"
      >
        <path
          d={calculateSlice(center, managedPercentage, 0)}
          className="fill-blue-500 hover:fill-blue-400 transition-all duration-200 cursor-pointer"
          onMouseOver={e => {
            (e.target as HTMLElement).setAttribute(
              'd',
              calculateSlice(center, managedPercentage, 0, true),
            );
          }}
          onMouseOut={e => {
            (e.target as HTMLElement).setAttribute(
              'd',
              calculateSlice(center, managedPercentage, 0, false),
            );
            setCursor(undefined);
          }}
          onMouseMove={e => {
            setCursor({ x: e.clientX, y: e.clientY, managed: true });
          }}
        />
        <path
          d={calculateSlice(
            center,
            unmanagedPercentage,
            managedPercentage * 3.6,
          )}
          className="fill-gray-300 hover:fill-gray-200 transition-all duration-200 cursor-pointer  dark:fill-gray-700 dark:hover:fill-gray-600"
          onMouseOver={e => {
            (e.target as HTMLElement).setAttribute(
              'd',
              calculateSlice(
                center,
                unmanagedPercentage,
                managedPercentage * 3.6,
                true,
              ),
            );
          }}
          onMouseOut={e => {
            (e.target as HTMLElement).setAttribute(
              'd',
              calculateSlice(
                center,
                unmanagedPercentage,
                managedPercentage * 3.6,
                false,
              ),
            );
            setCursor(undefined);
          }}
          onMouseMove={e => {
            setCursor({ x: e.clientX, y: e.clientY, managed: false });
          }}
        />
        {total === 0 ? (
          <text
            x={center}
            y={center}
            dy={2}
            className="text-[8px] fill-black dark:fill-white"
            dominantBaseline="middle"
            textAnchor="middle"
          >
            <tspan x={center} y={center} dy="-0.4em">
              No
            </tspan>
            <tspan x={center} y={center - 2} dy="0.8em">
              resources
            </tspan>
          </text>
        ) : (
          <text
            x={center}
            y={center + 2}
            className="text-[14px] fill-black dark:fill-white"
            dominantBaseline="middle"
            textAnchor="middle"
          >
            {Math.round(managedPercentage)}%
          </text>
        )}
      </svg>
      {cursor && (
        <div
          className="absolute px-2 py-1 rounded-lg shadow-md bg-background/95 opacity-95"
          style={{
            top: cursor.y,
            left: cursor.x,
            transform: 'translate(calc(0% + .5rem), calc(-100% - .5rem))',
          }}
        >
          <p className="dark:text-white text-black">
            <span
              className={
                cursor.managed
                  ? 'text-blue-500'
                  : 'text-gray-300 dark:text-gray-700'
              }
            >
              •{' '}
            </span>
            {cursor.managed ? (
              <FormattedMessage
                id="coverage.overview.charts.managed"
                values={{ count: managed }}
              />
            ) : (
              <FormattedMessage
                id="coverage.overview.charts.unmanaged"
                values={{ count: unmanaged }}
              />
            )}
          </p>
        </div>
      )}
    </>
  );
};
